import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { AppHeader } from "@/components/AppHeader";
import { MemoryCard } from "@/components/MemoryCard";
import { AddMemoryModal } from "@/components/AddMemoryModal";
import { MILESTONE_CATEGORIES } from "@/pages/Milestones";
import { useFamily } from "@/lib/useFamily";
import { formatDate } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MilestoneDetail() {
    const { milestoneId } = useParams();
    const navigate = useNavigate();
    const { checking, user, family } = useFamily();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/milestones/${milestoneId}`, { withCredentials: true });
            setData(data);
        } catch {
            setNotFound(true);
        }
    }, [milestoneId]);

    useEffect(() => {
        if (!checking) load();
    }, [checking, load]);

    const remove = async () => {
        try {
            await axios.delete(`${API}/milestones/${milestoneId}`, { withCredentials: true });
            toast.success("Milestone removed. Its memories stay in your archive.");
            navigate("/milestones", { replace: true });
        } catch {
            toast.error("Could not delete this milestone.");
        }
    };

    if (checking || (!data && !notFound)) {
        return (
            <main data-testid="milestone-detail-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    if (notFound) {
        return (
            <main data-testid="milestone-detail-not-found" className="min-h-screen bg-cream text-navy">
                <AppHeader user={user} />
                <div className="mx-auto max-w-3xl px-6 py-24 text-center">
                    <h1 className="font-display text-3xl">This milestone was not found.</h1>
                    <Link to="/milestones" data-testid="back-to-milestones" className="mt-4 inline-block text-sm font-medium underline-offset-4 hover:underline">
                        Back to milestones
                    </Link>
                </div>
            </main>
        );
    }

    const { milestone, memories, can_delete } = data;
    const cat = MILESTONE_CATEGORIES[milestone.category] || MILESTONE_CATEGORIES.other;

    return (
        <main data-testid="milestone-detail-page" className="min-h-screen bg-cream text-navy">
            <AppHeader user={user} />
            <div className="mx-auto max-w-6xl px-6 py-10">
                <Link
                    to="/milestones"
                    data-testid="back-to-milestones"
                    className="inline-flex items-center gap-2 text-sm font-medium text-navy/70 transition-colors duration-300 hover:text-navy"
                >
                    <ArrowLeft size={15} /> All milestones
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                    className="mt-6 overflow-hidden rounded-3xl border-2 border-warmgray bg-ivory"
                >
                    <div className="flex h-28 items-end bg-gradient-to-br from-lavender to-peach p-6">
                        <span className="flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-white backdrop-blur-sm">
                            <cat.icon size={13} /> {cat.label}
                        </span>
                    </div>
                    <div className="p-6 md:p-8">
                        <h1 data-testid="milestone-title" className="font-display text-3xl tracking-tight md:text-4xl">{milestone.title}</h1>
                        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#C98A94]">
                            {milestone.milestone_date ? `${formatDate(milestone.milestone_date)} · ` : ""}
                            Added by {milestone.created_by_name}
                        </p>
                        {milestone.description && (
                            <p data-testid="milestone-description" className="mt-4 max-w-2xl text-sm leading-relaxed text-[#2C3347]">
                                {milestone.description}
                            </p>
                        )}
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <button
                                data-testid="milestone-add-memory-button"
                                onClick={() => setModalOpen(true)}
                                className="btn-gradient flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                            >
                                <Plus size={16} /> Add a Memory
                            </button>
                            {can_delete &&
                                (confirming ? (
                                    <span className="flex items-center gap-2">
                                        <button
                                            data-testid="milestone-delete-confirm"
                                            onClick={remove}
                                            className="rounded-full bg-red-700 px-5 py-3 text-xs font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                                        >
                                            Confirm delete
                                        </button>
                                        <button
                                            data-testid="milestone-delete-cancel"
                                            onClick={() => setConfirming(false)}
                                            className="rounded-full border-2 border-warmgray px-5 py-3 text-xs font-semibold text-navy"
                                        >
                                            Keep it
                                        </button>
                                    </span>
                                ) : (
                                    <button
                                        data-testid="milestone-delete-button"
                                        onClick={() => setConfirming(true)}
                                        className="flex items-center gap-2 rounded-full border-2 border-warmgray px-6 py-3 text-sm font-semibold text-navy/70 transition-colors duration-300 hover:border-red-700 hover:text-red-700"
                                    >
                                        <Trash2 size={15} /> Delete milestone
                                    </button>
                                ))}
                        </div>
                    </div>
                </motion.div>

                {memories.length === 0 ? (
                    <div data-testid="milestone-memories-empty" className="mt-10 rounded-3xl border-2 border-dashed border-warmgray bg-ivory p-12 text-center">
                        <h2 className="font-display text-2xl">This chapter is waiting for its first memory.</h2>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-navy">
                            Add photos, a voice story, or a written memory — everyone in {family.name} can contribute.
                        </p>
                    </div>
                ) : (
                    <div data-testid="milestone-memories" className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {memories.map((m) => (
                            <MemoryCard key={m.memory_id} memory={m} />
                        ))}
                    </div>
                )}
            </div>

            <AddMemoryModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSaved={load}
                milestones={[milestone]}
                members={family.members}
                presetMilestoneId={milestone.milestone_id}
            />
        </main>
    );
}
